<template>
  <div class="map-container flex">
    <Teleport to="body">
      <div
        ref="popupButtonElement"
        class="fixed w-[48px] h-[48px] pointer-events-none"
      >
        <VDropdown
          ref="cardDropdown"
          :popper-class="{
            'card-dropdown': true,
            'max-md:hidden': false && !showMap
          }"
          placement="top"
          strategy="fixed"
          :triggers="[]"
          :shown="Boolean(selectedMarker)"
          :arrow-padding="32"
          :auto-hide="false"
        >
          <div
            class="w-[48px] h-[48px] pointer-events-none bg-no-repeat bg-center"
            :style="{
              backgroundImage: selectedMarker?.data?.markerImages?.[1]
                ? `url(${selectedMarker.data.markerImages[1]})`
                : null
            }"
          ></div>
          <template #popper>
            <div id="popup" class="z-[101]">
              <template
                v-if="
                  selectedMarker &&
                  selectedMarker.markerIndex !== undefined &&
                  (!getCardData || getCardData(selectedMarker.data.id))
                "
              >
                <SiteProjectCard
                  :favorite="favorites[selectedMarker.data.id]"
                  v-bind="
                    getCardData
                      ? getCardData(selectedMarker.data.id)
                      : selectedMarker.data
                  "
                  show-favorite
                  hide-carousel
                  mini-card
                  :data-name="`${selectedMarker.data.name}-card`"
                  :class="{
                    'pointer-events-none': !interactiveMarkers
                  }"
                  @click="
                    () => {
                      clickedPinModel = null;
                    }
                  "
                  @update:favorite="
                    () =>
                      favorites
                        ? setPropertyAsFavorite(selectedMarker.data, $event)
                        : null
                  "
                ></SiteProjectCard>
                <div
                  class="absolute top-0 right-0 min-w-[100px] min-h-[100px] pointer-events-none close-background"
                >
                  <button
                    v-if="selectedMarker"
                    type="button"
                    class="absolute top-0 right-0 p-2 pointer-events-auto"
                    @click="
                      () => {
                        selectedMarker = null;
                        updateMarkersAlpha();
                      }
                    "
                  >
                    <Icon :path="mdiClose" class="text-neutral/80 w-6" />
                  </button>
                </div>
              </template>
              <div
                v-else-if="
                  selectedMarker && selectedMarker.markerIndex !== undefined
                "
                class="w-[80px] h-[80px] flex items-end justify-center"
              >
                <div
                  class="flex items-center justify-center p-2 h-full w-full !z-[51]"
                >
                  <Icon
                    :path="mdiLoading"
                    class="text-neutral-light h-5 w-5 mx-auto animate-spin"
                  />
                </div>
              </div>
              <div
                v-else-if="
                  selectedMarker && selectedMarker.markerIndex === undefined
                "
                class="flex items-end justify-center"
              >
                <div
                  class="relative flex flex-col items-left justify-center p-4 min-w-[100px] max-w-[100%] !z-[51]"
                >
                  <button
                    v-if="selectedMarker"
                    type="button"
                    class="absolute top-1 right-1 p-1 text-neutral-light pointer-events-auto"
                    @click="
                      () => {
                        selectedMarker = null;
                        updateMarkersAlpha();
                      }
                    "
                  >
                    <Icon :path="mdiClose" class="text-neutral/80 h-4 w-4" />
                  </button>
                  <div class="leading-4 mb-2">
                    {{
                      selectedMarker?.name ||
                      selectedMarker?.data?.name ||
                      selectedMarker?.data?.mall
                    }}
                  </div>
                  <div class="text-sm leading-4">
                    {{ selectedMarker?.data?.address }}
                  </div>
                  <a
                    v-if="selectedMarker?.data?.website"
                    class="text-primary underline leading-4"
                    :href="selectedMarker.data.website"
                    target="_blank"
                  >
                    {{ $t('search.visitSite') }}
                  </a>
                </div>
              </div>
            </div>
          </template>
        </VDropdown>
      </div>
      <div
        ref="hoveredMarkerElement"
        class="fixed w-[48px] h-[48px] pointer-events-none bg-no-repeat bg-center"
        :class="{
          hidden: !hovered?.position || !hovered?.data?.markerImages?.[1]
        }"
        :style="{
          backgroundImage: hovered?.data?.markerImages?.[1]
            ? `url(${hovered.data.markerImages[1]})`
            : null
        }"
      ></div>
    </Teleport>
    <div
      id="cesium-map-container"
      class="inside-map relative flex items-center"
      @mouseover="
        () => {
          if (hoveredItem) {
            hoveredItemModel = null;
          }
        }
      "
    >
      <div id="cesium-container"></div>
      <div
        v-if="showPlacesSwitches && placesLayersModel"
        class="absolute right-2 bottom-2 w-[200px] bg-white rounded-lg shadow-md p-2"
      >
        <AppSwitch
          v-for="layer in placesOptions"
          :key="`layer-toggle-${layer.key}`"
          :model-value="placesLayersModel?.[layer.key]"
          :name="`layer-toggle-${layer.key}`"
          :title="$t(layer.title)"
          :label="$t(layer.title)"
          @update:model-value="placesLayersModel[layer.key] = $event"
        />
      </div>
      <!-- container in the upper-right corner -->
      <div class="absolute top-3 right-3 flex gap-2">
        <AppButton
          class="h-8 lg:h-10 px-2 text-sm lg:text-base rounded whitespace-nowrap"
          @click="tiltCamera"
        >
          {{ isTilted ? $t('search.view2D') : $t('search.view3D') }}
        </AppButton>
        <AppButton
          class="h-8 lg:h-10 px-2 text-sm lg:text-base rounded whitespace-nowrap"
          @click="switchImagery"
        >
          {{
            showBuildings ? $t('search.viewMap') : $t('search.viewBuildings')
          }}
        </AppButton>
        <AppButton
          class="w-8 lg:w-10 h-8 lg:h-10 pointer-events-auto rounded"
          @click="toggleFullscreen"
        >
          <Icon :path="mdiFullscreen" />
        </AppButton>
      </div>
      <!-- container in the middle-right -->
      <div class="absolute absolute-center-y right-3 flex flex-col gap-2">
        <AppButton class="w-8 lg:w-10 h-8 lg:h-10 rounded" @click="zoomIn">
          <Icon :path="mdiPlus" />
        </AppButton>
        <AppButton class="w-8 lg:w-10 h-8 lg:h-10 rounded" @click="zoomOut">
          <Icon :path="mdiMinus" />
        </AppButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts"></script>

<script setup lang="ts">
// import { onClickOutside } from '@vueuse/core';

import {
  mdiClose,
  mdiFullscreen,
  mdiHome,
  mdiLoading,
  mdiMinus,
  mdiOfficeBuilding,
  mdiPlus
} from '@mdi/js';
import { Dropdown as VDropdown } from 'floating-vue';

import { placesOptions } from '@/helpers/search';
import { useSearchStore } from '@/store/search';
import type {
  Layer,
  PlaceLayer,
  ProjectCardProps,
  ProjectCardPropsExtended
} from '@/types/properties';

const AppSwitch = defineAsyncComponent(
  () => import('@/async-components/App/Switch.vue')
);

type Cesium = typeof import('cesium');

interface Place {
  name: string;
  address: string;
  lat: number;
  lng: number;
}

type Entity =
  | (InstanceType<Cesium['Entity']> & {
      markerIndex: number;
      data: ProjectCardProps & { markerImages?: string[] };
      group: Layer;
    })
  | (InstanceType<Cesium['Entity']> & {
      markerIndex: undefined;
      data: Place & { markerImages?: string[] };
      group: Layer;
    });

const MAX_HEIGHT = 250;

const SitePropertyCardPlaceholder = defineAsyncComponent(
  () => import('@/async-components/Site/PropertyCardPlaceholder.vue')
);

const SiteProjectCard = defineAsyncComponent({
  loader: () => import('@/async-components/Site/ProjectCard.vue'),
  loadingComponent: SitePropertyCardPlaceholder,
  delay: 1
});

type Emits = {
  (e: 'update:favorite', property: ProjectCardProps, value: boolean): void;
  (e: 'clearPolygon'): void;
  (e: 'pushCustomPolygon', paths: google.maps.LatLng[][]): void;
  (e: 'triggerSearch'): void;
};

const emit = defineEmits<Emits>();

const {
  public: { apiUrl, gMapsKey }
} = useRuntimeConfig();

const searchStore = useSearchStore();

const { get } = useHttpMethods();

const props = withDefaults(
  defineProps<{
    center?: { lat: number; lng: number };
    markers: ProjectCardPropsExtended[];
    interactiveMarkers?: boolean;
    placesLayers: Record<PlaceLayer, boolean>;
    showPlacesSwitches?: boolean;
    storePosition?: boolean;
    showMap?: boolean;
    searchParams?: string[];
    favorites?: Record<string, boolean>;
    clickedPin?: string | null;
    hoveredItem?: string | null;
    getCardData?: (id: string) => ProjectCardProps | null;
  }>(),
  {
    placesLayers: () => ({
      beaches: false,
      malls: false,
      restaurants: false,
      'college-universities': false,
      'public-schools': false,
      'private-schools': false,
      hospitals: false
    })
  }
);

const placesLayersModel = defineModel<Record<Layer, boolean>>('placesLayers', {
  default: () => ({
    beaches: false,
    malls: false,
    restaurants: false,
    'college-universities': false,
    'public-schools': false,
    'private-schools': false,
    hospitals: false
  })
});

const clickedPinModel = defineModel<string | null>('clickedPin', {
  default: null
});

const hoveredItemModel = defineModel<string | null>('hoveredItem', {
  default: null
});

const selectedMarker = ref<Entity | null>(null);

type ViewerInstace = InstanceType<Cesium['Viewer']>;

const viewer = ref<ViewerInstace | null>(null);

const popupButtonElement = ref<HTMLElement | null>(null);
const hoveredMarkerElement = ref<HTMLElement | null>(null);

const cardDropdown = ref<{
  onResize: () => void;
}>();

// onClickOutside(popupButtonElement, () => {
//   if (popupButtonElement.value) {
//     popupButtonElement.value.style.display = 'none';
//   }
// });

function getCesium() {
  return new Promise<Cesium>((resolve, reject) => {
    if (
      typeof window !== 'undefined' &&
      (window as unknown as { Cesium: Cesium }).Cesium
    ) {
      resolve((window as unknown as { Cesium: Cesium }).Cesium);
      return;
    }

    const script = document.createElement('script');
    script.src =
      'https://ajax.googleapis.com/ajax/libs/cesiumjs/1.105/Build/Cesium/Cesium.js';
    script.defer = true;
    document.head.appendChild(script);

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://ajax.googleapis.com/ajax/libs/cesiumjs/1.105/Build/Cesium/Widgets/widgets.css';
    document.head.appendChild(link);

    script.onload = () => {
      if (typeof window === 'undefined') {
        reject(new Error('Window is not defined'));
      }
      resolve((window as unknown as { Cesium: Cesium }).Cesium);
    };

    script.onerror = e => {
      reject(e);
    };
  });
}

function createMarkerFromSvg(
  svgPath: string,
  color: string = '#fff',
  backgroundColors: string[] = ['#102032', '#e2007c'],
  size: number = 36,
  padding: number = 4
) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const circleSize = size + padding * 2;

  canvas.width = canvas.height = circleSize;

  return backgroundColors.map(backgroundColor => {
    ctx.clearRect(0, 0, circleSize, circleSize);
    ctx.resetTransform();

    // Draw circular background
    ctx.fillStyle = backgroundColor;
    ctx.beginPath();
    ctx.arc(circleSize / 2, circleSize / 2, circleSize / 2, 0, 2 * Math.PI);
    ctx.fill();

    // Draw the SVG path onto the canvas
    ctx.fillStyle = color;
    ctx.translate(padding, padding);
    ctx.scale(size / 24, size / 24);
    ctx.fill(new Path2D(svgPath));

    // Convert the canvas to a data URL and return
    return canvas.toDataURL();
  });
}

async function updateMarkers() {
  const Cesium = await getCesium();

  if (!Cesium) {
    console.error('Cesium is not defined');
    return;
  }

  if (!viewer.value) {
    console.error('Viewer is not defined');
    return;
  }

  // clear all markers

  viewer.value.entities.removeAll();

  if (!props.markers?.length) {
    return;
  }

  // update from markers prop

  const buildingMarker = createMarkerFromSvg(mdiOfficeBuilding);
  const houseMarker = createMarkerFromSvg(mdiHome);

  props.markers.forEach((marker, index) => {
    viewer.value.entities.add({
      name: marker.name,
      position: Cesium.Cartesian3.fromDegrees(
        marker.position.lng,
        marker.position.lat,
        20.1 + index * 0.1
      ),
      billboard: {
        image: marker.type === 'condo' ? buildingMarker[0] : houseMarker[0],
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM
      },
      group: 'new-projects',
      markerIndex: index,
      data: {
        ...marker,
        markerImages: marker.type === 'condo' ? buildingMarker : houseMarker
      }
    });
  });

  updateLayers();

  return;

  // Calculate min and max coordinates for your pins
  const minMax = props.markers.reduce(
    (acc, marker) => {
      if (marker.position.lat < acc.minLat) {
        acc.minLat = marker.position.lat;
      }
      if (marker.position.lat > acc.maxLat) {
        acc.maxLat = marker.position.lat;
      }
      if (marker.position.lng < acc.minLng) {
        acc.minLng = marker.position.lng;
      }
      if (marker.position.lng > acc.maxLng) {
        acc.maxLng = marker.position.lng;
      }
      return acc;
    },
    {
      minLat: Infinity,
      maxLat: -Infinity,
      minLng: Infinity,
      maxLng: -Infinity
    }
  );

  const height = Math.max(
    Cesium.Cartesian3.distance(
      Cesium.Cartesian3.fromDegrees(minMax.minLng, minMax.minLat),
      Cesium.Cartesian3.fromDegrees(minMax.maxLng, minMax.minLat)
    ),
    Cesium.Cartesian3.distance(
      Cesium.Cartesian3.fromDegrees(minMax.minLng, minMax.minLat),
      Cesium.Cartesian3.fromDegrees(minMax.minLng, minMax.maxLat)
    )
  );

  const averagePosition = Cesium.Cartesian3.fromDegrees(
    (minMax.minLng + minMax.maxLng) / 2,
    (minMax.minLat + minMax.maxLat) / 2,
    height
  );

  viewer.value.camera.flyTo({
    destination: averagePosition,
    duration: 2.0, // Animation duration
    offset: new Cesium.HeadingPitchRange(0, -Math.PI / 4, 100.0) // Adjust offset
  });
}

const isTilted = ref(false);

async function tiltCamera() {
  const Cesium = await getCesium();

  let tiltAngle;

  if (isTilted.value) {
    // Set pitch for top-down view
    tiltAngle = Cesium.Math.toRadians(-90); // -90 degrees for straight down
  } else {
    // Set pitch for 25-degree tilt
    tiltAngle = Cesium.Math.toRadians(-25); // -25 degrees for the upward tilt
  }

  // Determine the camera's current target point
  const ray = viewer.value.camera.getPickRay(
    new Cesium.Cartesian2(
      viewer.value.canvas.width / 2,
      viewer.value.canvas.height / 2
    )
  );
  const targetPoint = viewer.value.scene.globe.pick(ray, viewer.value.scene);

  if (!targetPoint) {
    return;
  }

  const range = Cesium.Cartesian3.distance(
    viewer.value.camera.positionWC,
    targetPoint
  );
  const heading = viewer.value.camera.heading;

  // Use lookAt with the target point and the specified orientation
  viewer.value.camera.lookAt(
    targetPoint,
    new Cesium.HeadingPitchRange(heading, tiltAngle, range)
  );

  // To keep the "lookAt" transformation, apply it with an identity matrix
  viewer.value.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);

  // Toggle the tilt state
  isTilted.value = !isTilted.value;
}

const showBuildings = ref(false);

type ImageryLayer = InstanceType<Cesium['ImageryLayer']>;
type Cesium3DTileset = InstanceType<Cesium['Cesium3DTileset']>;

let openStreetMapLayer: ImageryLayer | null = null;
let google3DTileset: Cesium3DTileset | null = null;

async function switchImagery() {
  showBuildings.value = !showBuildings.value;
  await updateImagery();
}

async function updateImagery() {
  const Cesium = await getCesium();

  if (showBuildings.value) {
    if (!google3DTileset) {
      google3DTileset = new Cesium.Cesium3DTileset({
        url: 'https://tile.googleapis.com/v1/3dtiles/root.json?key=AIzaSyDbhjlXscaVROU0p57C7IW85W6Qunfaabw',
        showCreditsOnScreen: true
      });

      viewer.value.scene.primitives.add(google3DTileset);

      // Hide the OpenStreetMap layer only if the 3D buildings are not loaded yet

      openStreetMapLayer.show = false;
    }
    google3DTileset.show = true;
  } else {
    if (google3DTileset) {
      google3DTileset.show = false;
      openStreetMapLayer.show = true;
    }
    showBuildings.value = false;
  }

  nextTick(() => {
    viewer.value.scene.requestRender();
  });
}

async function setCameraHeight(height: number) {
  const Cesium = await getCesium();
  viewer.value.camera.flyTo({
    destination: Cesium.Cartesian3.fromRadians(
      viewer.value.camera.positionCartographic.longitude,
      viewer.value.camera.positionCartographic.latitude,
      height
    ),
    duration: 1.0
  });
}

function toggleFullscreen() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  if (document.fullscreenElement || window.compatibleFullscreenElement) {
    window.compatibleFullscreenElement = null;

    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  } else {
    const element = document.getElementById('cesium-map-container');
    window.compatibleFullscreenElement = element;

    if (element?.requestFullscreen) {
      element.requestFullscreen();
    } else if (element?.webkitRequestFullscreen) {
      element.webkitRequestFullscreen();
    } else if (element?.mozRequestFullScreen) {
      element.mozRequestFullScreen();
    } else if (element?.msRequestFullscreen) {
      element.msRequestFullscreen();
    }
  }
}

function zoomIn() {
  let newHeight = viewer.value.camera.positionCartographic.height / 2;

  if (newHeight < 250) {
    newHeight = 250;
  }

  setCameraHeight(newHeight);
}

function zoomOut() {
  const newHeight = viewer.value.camera.positionCartographic.height * 2;
  setCameraHeight(newHeight);
}

let removableMarker = true;

async function updateMarkersAlpha() {
  const Cesium = await getCesium();

  if (!Cesium) {
    return;
  }

  const visibleColor = Cesium.Color.fromCssColorString('#ffffffff');
  const invisibleColor = Cesium.Color.fromCssColorString('#ffffff01');

  viewer.value.entities.values.forEach(entity => {
    if (entity.billboard) {
      entity.billboard.color = visibleColor;
    }
  });

  if (
    selectedMarker.value &&
    selectedMarker.value.data.markerImages.length > 1
  ) {
    selectedMarker.value.billboard.color = invisibleColor;
  }
  if (hovered.value && hovered.value.data.markerImages.length > 1) {
    hovered.value.billboard.color = invisibleColor;
  }

  viewer.value.scene.requestRender();
}

async function updateHtmlMarkersPosition(
  markerRef: Ref<Entity | null>,
  buttonRef: Ref<HTMLElement>
) {
  cardDropdown.value?.onResize();

  const Cesium = await getCesium();

  if (!Cesium || !markerRef.value?.position || !buttonRef.value) {
    return;
  }

  const button = buttonRef.value;

  const markerPosition = markerRef.value.position.getValue(
    Cesium.JulianDate.now()
  );

  const screenPosition = Cesium.SceneTransforms.wgs84ToWindowCoordinates(
    viewer.value.scene,
    markerPosition
  );

  if (!screenPosition) {
    return;
  }

  const cesiumElement = document.getElementById('cesium-container');

  const cesiumContainerElement = document.getElementById(
    'cesium-map-container'
  );

  const cesiumRect = cesiumElement!.getBoundingClientRect();

  const containerRect = cesiumContainerElement!.getBoundingClientRect();

  const newPosition = {
    x: screenPosition.x + cesiumRect!.x,
    y: screenPosition.y + cesiumRect!.y
  };

  newPosition.x -= 0;
  newPosition.y -= 23;

  if (
    removableMarker &&
    (newPosition.x < containerRect!.left + 19 - 6 ||
      newPosition.x > containerRect!.right - 19 + 6 ||
      newPosition.y < containerRect!.top + 21 - 6 ||
      newPosition.y > containerRect!.bottom - 21 + 6)
  ) {
    markerRef.value = null;
    updateMarkersAlpha();
    return;
  }

  newPosition.x = Math.min(
    containerRect.right - 19,
    Math.max(containerRect.left + 19, newPosition.x)
  );
  newPosition.y = Math.min(
    containerRect.bottom - 23,
    Math.max(containerRect.top + 21, newPosition.y)
  );

  newPosition.x -= 24;
  newPosition.y -= 23;

  button.style.top = `${newPosition.y}px`;
  button.style.left = `${newPosition.x}px`;
}

async function updatePopupsHeight() {
  const Cesium = await getCesium();

  if (!Cesium || !viewer.value) {
    return;
  }

  // Determine the camera's height above the ellipsoid
  const cameraHeight = viewer.value.camera.positionCartographic.height;

  // Desired marker altitude based on camera height
  const markerAltitude = showBuildings.value
    ? cameraHeight > 2000
      ? 0
      : 0
    : 2;

  const markerAltitudeMultiplier =
    markerAltitude < 2000.0 ? 0.1 : markerAltitude / 400.0;

  // Iterate through each marker and update its altitude
  viewer.value.entities.values.forEach((entity, index) => {
    if (entity.billboard) {
      // Check if the entity is a marker
      const currentPosition = entity.position.getValue(Cesium.JulianDate.now());
      const cartographicPosition =
        Cesium.Cartographic.fromCartesian(currentPosition);

      // Update the altitude of the position
      cartographicPosition.height =
        markerAltitude + index * markerAltitudeMultiplier;

      // Update the entity's position with the new altitude
      entity.position = Cesium.Cartesian3.fromRadians(
        cartographicPosition.longitude,
        cartographicPosition.latitude,
        cartographicPosition.height
      );
    }
  });

  // Update popup position if a marker is selected
  if (selectedMarker.value?.position) {
    const newPosition = selectedMarker.value.position.getValue(
      Cesium.JulianDate.now()
    );
    const screenPosition = Cesium.SceneTransforms.wgs84ToWindowCoordinates(
      viewer.value.scene,
      newPosition
    );
    if (screenPosition) {
      updateHtmlMarkersPosition(selectedMarker, popupButtonElement);
    }
  }
}

let cameraMoving = false;

async function centerToSelectedMarker() {
  const Cesium = await getCesium();

  if (!Cesium || !selectedMarker.value) {
    return;
  }

  const position = selectedMarker.value.position.getValue(
    Cesium.JulianDate.now()
  );
  const cartographicPosition = Cesium.Cartographic.fromCartesian(position);

  const newPosition = Cesium.Cartesian3.fromRadians(
    cartographicPosition.longitude,
    cartographicPosition.latitude,
    viewer.value.camera.positionCartographic.height
  );

  cameraMoving = true;

  if (isTilted.value) {
    const height = viewer.value.camera.positionCartographic.height;

    const angleInRadians = Cesium.Math.toRadians(-25);

    const heightChange = height * Math.tan(angleInRadians);

    const latitudeChange = (heightChange / 11113) * (Math.PI / 180);

    const newLatitude = cartographicPosition.latitude + latitudeChange;

    const newCameraPosition = Cesium.Cartesian3.fromRadians(
      cartographicPosition.longitude,
      newLatitude,
      height
    );

    viewer.value.camera.flyTo({
      destination: newCameraPosition,
      orientation: {
        heading: Cesium.Math.toRadians(0),
        pitch: angleInRadians,
        roll: 0.0
      },
      duration: 1.0,
      complete: () => {
        removableMarker = true;
        cameraMoving = false;
      }
    });
  } else {
    viewer.value.camera.flyTo({
      destination: newPosition,
      duration: 1.0,
      complete: () => {
        removableMarker = true;
        cameraMoving = false;
      }
    });
  }
}

async function saveCameraPositionAndOrientation() {
  if (!props.storePosition) {
    return;
  }

  const Cesium = await getCesium();

  await new Promise(resolve => setTimeout(resolve, 100));

  const camera = viewer.value?.scene?.camera;

  if (!Cesium || !camera || !searchStore) {
    return;
  }

  // Get the camera's current position in Cartesian coordinates

  const position = camera.positionCartographic;
  const longitude = Cesium.Math.toDegrees(position.longitude);
  const latitude = Cesium.Math.toDegrees(position.latitude);
  const height = position.height;

  // Get the camera's heading, pitch, and roll
  const heading = camera.heading;
  const pitch = camera.pitch;
  const roll = camera.roll;

  // Save the camera's position and orientation to the store

  searchStore.setCamera({
    longitude,
    latitude,
    height,
    heading,
    pitch,
    roll
  });
}

const hovered = ref<Entity | null>(null);

async function initializeMap() {
  const Cesium = await getCesium();

  if (!Cesium) {
    console.error('Cesium is not defined');
    return;
  }

  Cesium.Ion.defaultAccessToken = gMapsKey;

  viewer.value = new Cesium.Viewer('cesium-container', {
    imageryProvider: new Cesium.OpenStreetMapImageryProvider({
      url: 'https://a.tile.openstreetmap.org/'
    }),
    baseLayerPicker: false,
    requestRenderMode: true,
    navigationInstructionsInitiallyVisible: false,
    geocoder: false,
    homeButton: false,
    fullscreenButton: false,
    navigationHelpButton: false,
    sceneModePicker: false,
    timeline: false,
    animation: false
  });

  // get imagery layer

  openStreetMapLayer = viewer.value.imageryLayers.get(0);
  // openStreetMapLayer.show = false;

  updateImagery();

  viewer.value.scene.globe.baseColor =
    Cesium.Color.fromCssColorString('#4b6099');

  // show sky
  viewer.value.scene.skyAtmosphere.show = true;

  viewer.value.scene.skyBox.show = true;

  viewer.value.scene.screenSpaceCameraController.minimumZoomDistance =
    MAX_HEIGHT;

  updateMarkers();

  const handler = new Cesium.ScreenSpaceEventHandler(viewer.value.scene.canvas);

  handler.setInputAction(function (click) {
    const pickedObject = viewer.value.scene.pick(click.position);

    if (Cesium.defined(pickedObject) && pickedObject.id) {
      selectedMarker.value = pickedObject.id;

      // Hide the image of the selected marker
      updateMarkersAlpha();
      updateHtmlMarkersPosition(selectedMarker, popupButtonElement);
      viewer.value.selectedEntity = undefined;
    }
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

  // on hover...

  handler.setInputAction(function (movement) {
    const hoveredObject = viewer.value.scene.pick(movement.endPosition);

    if (hoveredObject?.id && hoveredObject?.id?.id === hovered.value?.id) {
      return;
    }

    if (Cesium.defined(hoveredObject) && hoveredObject?.id) {
      // Restore previous marker image

      hovered.value = hoveredObject.id as Entity;
      updateMarkersAlpha();
      updateHtmlMarkersPosition(hovered, hoveredMarkerElement);

      viewer.value.scene.canvas.style.cursor = 'pointer';
    } else if (hovered.value) {
      // Unset cursor
      viewer.value.scene.canvas.style.cursor = 'unset';

      // Unset hovered value
      hovered.value = null;

      updateMarkersAlpha();
    } else {
      viewer.value.scene.requestRender();
    }
  }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

  // event while the camera is moving

  let cameraDragging = false;

  viewer.value.scene.camera.moveStart.addEventListener(function () {
    cameraDragging = true;
  });

  viewer.value.scene.camera.moveEnd.addEventListener(function () {
    cameraDragging = false;
    saveCameraPositionAndOrientation();

    const pitch = Cesium.Math.toDegrees(viewer.value.scene.camera.pitch);

    if (isTilted.value && pitch < -80 && pitch > -91) {
      // Threshold to consider camera not tilted
      isTilted.value = false;
    } else if (!isTilted.value && pitch < -20 && pitch > -80) {
      // Threshold to consider camera tilted
      isTilted.value = true;
    }

    // const position = viewer.value.scene.camera.positionCartographic;
    // const longitude = Cesium.Math.toDegrees(position.longitude);
    // const latitude = Cesium.Math.toDegrees(position.latitude);
    // const height = viewer.value.scene.camera.positionCartographic.height;
    // console.log('[DEBUG] Camera position:', {
    //   height,
    //   pitch,
    //   latitude,
    //   longitude,
    //   position
    // });
  });

  window.addEventListener('scroll', () => {
    cameraDragging = true;
    setTimeout(() => {
      cameraDragging = false;
    }, 10);
  });

  viewer.value.scene.postRender.addEventListener(function () {
    if (!cameraMoving && !cameraDragging) return;
    updateHtmlMarkersPosition(hovered, hoveredMarkerElement);
    updateHtmlMarkersPosition(selectedMarker, popupButtonElement);
    updatePopupsHeight();
  });

  const storeCamera = props.storePosition ? searchStore?.camera : null;

  const lngDiff = 0.0; // 0.007
  const latDiff = 0.0; // 0.015

  const cameraPosition = Cesium.Cartesian3.fromDegrees(
    storeCamera?.longitude ||
      (props.center?.lng ? props.center.lng - lngDiff : null) ||
      -80.198321,
    storeCamera?.latitude ||
      (props.center?.lat ? props.center.lat - latDiff : null) ||
      25.754376,
    storeCamera?.height || 15000
  );
  const heading = storeCamera?.heading || Cesium.Math.toRadians(0);
  const pitch = storeCamera?.pitch || Cesium.Math.toRadians(-90);
  const roll = storeCamera?.roll || 0.0;

  viewer.value.camera.setView({
    destination: cameraPosition,
    orientation: {
      heading,
      pitch,
      roll
    }
  });

  selectedMarker.value = null;
  updateMarkersAlpha();
}

const markerIcons = Object.fromEntries(
  placesOptions.map(layer => [layer.key, layer.icon])
) as Record<PlaceLayer, string>;

const loadedLayers = ref<string[]>([]);

async function loadLayer(layer: PlaceLayer) {
  if (layer in loadedLayers.value) {
    return;
  }

  const response = await get<Place[]>(
    `${apiUrl}/city-infrastructure/?layer=${layer}`
  );

  const Cesium = await getCesium();

  if (response.length) {
    loadedLayers.value.push(layer);
  }

  const markerImage = createMarkerFromSvg(markerIcons[layer]);

  response.forEach((marker, index) => {
    viewer.value.entities.add({
      name: marker.name,
      position: Cesium.Cartesian3.fromDegrees(
        marker.lng,
        marker.lat,
        20.0 + index * 0.1
      ),
      billboard: {
        image: markerImage[0],
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM
      },
      group: layer,
      data: { ...marker, markerImages: markerImage },
      markerIndex: undefined
    });
  });
}

const removedEntities = ref<Cesium['Entity'][]>([]);

async function updateLayers() {
  // Get the layers that are currently visible, request the data for them if they haven't been loaded yet

  await Promise.all(
    Object.entries(props.placesLayers || {})
      .filter(([key, show]) => show && key !== 'new-projects')
      .map(([key]) => loadLayer(key as PlaceLayer))
  );

  // Create an array with both the entities that are in the visible layers and the entities that are not

  if (!viewer.value?.entities) {
    return;
  }

  const entities = [...viewer.value.entities.values, ...removedEntities.value];

  viewer.value.entities.removeAll();

  removedEntities.value = [];

  entities.forEach((entity: Entity) => {
    const add =
      entity.group === 'new-projects' ||
      (props.placesLayers &&
        props.placesLayers[
          entity.group in props.placesLayers ? entity.group : null
        ]);

    if (add) {
      viewer.value.entities.add(entity);
    } else {
      removedEntities.value.push(entity);
    }
  });
  viewer?.value?.scene?.requestRender();

  // Close the popup
  selectedMarker.value = null;
  updateMarkersAlpha();
}

watch(() => props.placesLayers, updateLayers, {
  immediate: true,
  deep: true
});

watch(showBuildings, () => {
  updatePopupsHeight();
});

function setPropertyAsFavorite(marker: ProjectCardProps, isFavorite: boolean) {
  emit('update:favorite', marker, isFavorite);
}

watch(
  () => props.markers,
  () => {
    setTimeout(() => {
      updateMarkers();
    }, 100);
  },
  {
    deep: true,
    immediate: true,
    flush: 'post'
  }
);

let focusItemTimeout: ReturnType<typeof setTimeout> | null = null;

watch(
  () => props.hoveredItem,
  (value, previousValue) => {
    if (focusItemTimeout) {
      clearTimeout(focusItemTimeout);
    }

    if (value) {
      removableMarker = false;

      const entity = (viewer.value?.entities?.values as Entity[]).find(
        marker =>
          marker.markerIndex !== undefined &&
          marker.data.id === props.hoveredItem
      ) as Entity;

      selectedMarker.value = entity;

      if (selectedMarker.value) {
        updateHtmlMarkersPosition(selectedMarker, popupButtonElement);
        const hoveredItemId = value;
        nextTick(() => {
          focusItemTimeout = setTimeout(() => {
            if (hoveredItemId !== props.hoveredItem) {
              return;
            }
            centerToSelectedMarker();
          }, 100);
        });
      }
    } else if (previousValue) {
      selectedMarker.value = null;
      updateMarkersAlpha();
    }
  }
);

watch(selectedMarker, () => {
  // update clicked pin to the id of the selected marker
  if (selectedMarker.value && selectedMarker.value.markerIndex) {
    clickedPinModel.value = selectedMarker.value.data.id;
  }
});

onMounted(async () => {
  const event = await getCesium();
  initializeMap();
});

defineExpose({
  updateMap: (useListener: boolean) => {
    // console.log(useListener);
  }
});
</script>

<style scoped lang="scss">
#cesium-container {
  width: 100%;
  height: 100vh;
  display: block;
}

#popupCloseBtn {
  cursor: pointer;
  position: absolute;
  top: 0;
  right: 5px;
  font-size: 18px;
  font-weight: bold;
}
.close-background {
  background: radial-gradient(
    circle at top right,
    rgba(255, 255, 255, 0.5) 12.5%,
    transparent 50%
  );
  border-top-right-radius: 20px;
}
</style>
